import { useRef, useState } from "react";
import { useLanguage } from "../i18n/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getSessionId = () => {
  let id = localStorage.getItem("un_chat_session");
  if (!id) {
    id = crypto.randomUUID();
    localStorage.setItem("un_chat_session", id);
  }
  return id;
};

const newMsg = (role, content) => ({ id: crypto.randomUUID(), role, content });

const appendToLast = (list, text) => {
  const copy = [...list];
  const last = copy[copy.length - 1];
  copy[copy.length - 1] = { ...last, content: last.content + text };
  return copy;
};

// Lee el stream SSE y entrega cada fragmento de texto a onDelta
const readSse = async (body, onDelta) => {
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  for (;;) {
    const { done, value } = await reader.read();
    if (done) return;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop();
    for (const part of parts) {
      const line = part.trim();
      if (!line.startsWith("data:")) continue;
      const data = line.slice(5).trim();
      if (data === "[DONE]") continue;
      const parsed = JSON.parse(data);
      if (parsed.error) throw new Error("llm");
      if (parsed.t) onDelta(parsed.t);
    }
  }
};

export const useChat = () => {
  const { lang, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const sessionId = useRef(getSessionId());

  const openChat = () => {
    setOpen(true);
    setMessages((m) => (m.length ? m : [newMsg("assistant", t.chat.welcome)]));
  };

  const send = async (raw) => {
    const text = (raw ?? input).trim();
    if (!text || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, newMsg("user", text)]);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionId.current, message: text, lang }),
      });
      if (!res.ok || !res.body) throw new Error("bad response");
      setMessages((m) => [...m, newMsg("assistant", "")]);
      await readSse(res.body, (delta) => setMessages((m) => appendToLast(m, delta)));
    } catch {
      setMessages((m) => {
        const last = m[m.length - 1];
        if (last?.role === "assistant" && !last.content) return [...m.slice(0, -1), { ...last, content: t.chat.error }];
        return [...m, newMsg("assistant", t.chat.error)];
      });
    } finally {
      setBusy(false);
    }
  };

  const last = messages[messages.length - 1];
  const showTyping = busy && (last?.role === "user" || (last?.role === "assistant" && !last.content));

  return { open, openChat, closeChat: () => setOpen(false), messages, input, setInput, busy, send, showTyping };
};
